import { Archive, Cancel, Delete, Edit, Map, Save, TravelExplore, ContentCopy } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Autocomplete,
} from "@mui/material";
import DispatchPingLocation from "components/dispatch/board/DispatchPingLocation";
import TextLocateForm from "components/dispatch/board/TextLocateForm";
import { setupWebSocket } from "components/helpers/WebSocketSetup";
import Header from "components/layout/Header";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import {
  useAddCardToBookedColumnMutation,
  useArchiveCardMutation,
  useDeleteCardMutation,
  useRemoveCardFromColumnMutation,
  useUpdateCardMutation,
  useUpdateColumnMutation,
  useGetCustomerPortalCompaniesQuery,
} from "state/dispatchApi";

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const PREDEFINED_COLORS = [
  { name: "Default", color: "#000000" },
  { name: "Sergio Rosa", color: "#d961a2" },
  { name: "Tori Siegal", color: "#8e7cc3" },
  { name: "Morgan Hodges", color: "#0e9602" },
  { name: "Dispatch 1", color: "#6d9eeb" },
  { name: "Dispatch 2", color: "#76a5af" },
  { name: "Other", color: "#a0522d" },
  { name: "Trouble", color: "#ff0000" },
  { name: "Assign Load", color: "#bbb85b" },
];

const DispatchCard = React.memo(({
  item,
  isEditing,
  onEditStart,
  onEditCancel,
  onSave,
  onDelete,
  onArchive,
  onRemove,
  onToggleTextLocate,
  onToggleLocation,
  provided,
  defaultCardColor,
  columnId,
}) => {
  const [editedTitle, setEditedTitle] = useState("");
  const [editedSubtitle, setEditedSubtitle] = useState("");
  const [editedNotes, setEditedNotes] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedColor, setEditedColor] = useState("");
  const [editedCompanyId, setEditedCompanyId] = useState("");
  const [editedCompanyName, setEditedCompanyName] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  
  const debouncedCompanySearch = useDebouncedValue(companySearch, 1000);

  const { data: companiesData, isFetching: companiesLoading } =
    useGetCustomerPortalCompaniesQuery(
      { search: debouncedCompanySearch, page: 1, limit: 20 },
      { skip: !isEditing }
    );

  const companyOptions = companiesData?.data ?? [];
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isEditing) {
      setEditedTitle(item.title || "");
      setEditedSubtitle(item.subtitle || "");
      setEditedNotes(item.notes || "");
      setEditedDescription(item.description || "");
      setEditedColor(item.color || defaultCardColor);
      setEditedCompanyId(item.companyId || "");
      setEditedCompanyName(item.companyName || "");
      setCompanySearch(item.companyName || "");
    }
  }, [isEditing, item, defaultCardColor]);

  const handleSave = () => {
    onSave(item._id, {
      title: editedTitle,
      subtitle: editedSubtitle,
      notes: editedNotes,
      description: editedDescription,
      color: editedColor,
      companyId: editedCompanyId || null,
      companyName: editedCompanyName || "",
    });
  };

  const handleColorChange = (colorHex) => {
    setEditedColor(colorHex);
  };

  const getDescriptionValue = (description, key) => {
    if (!description) return null;
    const pairs = description.split(/[,\n]/);
    for (const pair of pairs) {
      const [k, ...vParts] = pair.split(":");
      if (k && k.trim().toLowerCase() === key.toLowerCase()) {
        return vParts.join(":").trim();
      }
    }
    return null;
  };

  const handleCopyToClipboard = (text, label) => {
    if (!text) {
      enqueueSnackbar(`${label} not found`, { variant: "warning" });
      return;
    }
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied!`, { variant: "success", autoHideDuration: 3000 });
  };

  const colors = [
    { name: "Default", color: defaultCardColor },
    ...PREDEFINED_COLORS.filter(c => c.name !== "Default")
  ];

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      <Paper
        elevation={isEditing ? 10 : 3}
        sx={{ backgroundColor: isEditing ? editedColor : item.color }}
      >
        <div style={{ padding: "8px", marginBottom: "5px" }}>
          {isEditing ? (
            <div>
              <TextField
                fullWidth
                autoFocus
                label="Title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                variant="standard"
                multiline
              />
              <TextField
                fullWidth
                label="Subtitle"
                value={editedSubtitle}
                onChange={(e) => setEditedSubtitle(e.target.value)}
                variant="standard"
              />
              <TextField
                fullWidth
                label="Notes"
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                variant="standard"
                multiline
              />
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  style: {
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    wordBreak: "break-all",
                  },
                }}
              />
              <Autocomplete
                options={companyOptions}
                getOptionLabel={(opt) => opt?.name || ""}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                openOnFocus
                filterOptions={(x) => x}
                value={
                  editedCompanyId
                    ? { id: editedCompanyId, name: editedCompanyName }
                    : null
                }
                onChange={(_e, val) => {
                  setEditedCompanyId(val?.id || "");
                  setEditedCompanyName(val?.name || "");
                }}
                inputValue={companySearch}
                onInputChange={(_e, val) => setCompanySearch(val)}
                loading={companiesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    variant="standard"
                    placeholder="Search companies…"
                    fullWidth
                    sx={{ mt: 1, mb: 1 }}
                  />
                )}
              />
              <div>
                {colors.map((predefinedColor) => (
                  <Tooltip
                    title={predefinedColor.name}
                    key={predefinedColor.name}
                    placement="top"
                  >
                    <Button
                      style={{
                        backgroundColor: predefinedColor.color,
                        margin: "5px",
                        color: "white",
                      }}
                      onClick={() => handleColorChange(predefinedColor.color)}
                    >
                      {predefinedColor.name}
                    </Button>
                  </Tooltip>
                ))}
              </div>
              <Tooltip title="Save" placement="top">
                <IconButton onClick={handleSave} size="small">
                  <Save />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel" placement="top">
                <IconButton onClick={onEditCancel} size="small">
                  <Cancel />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Typography 
                  variant="h6" 
                  component="h3" 
                  gutterBottom
                  sx={{ wordBreak: "break-word" }}
                >
                  {item.title}
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom
                  sx={{ wordBreak: "break-word" }}
                >
                  {item.subtitle}
                </Typography>
                {item.archivedBy && (
                  <>
                    <Divider sx={{ color: "black", m: "10px 0px" }} />
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      fontWeight="bold"
                    >
                      ARCHIVED
                    </Typography>
                  </>
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <input
                  type="checkbox"
                  checked={isExpanded || false}
                  onChange={() => setIsExpanded(!isExpanded)}
                />
                <Tooltip title="Ping Driver" placement="bottom">
                  <IconButton onClick={() => onToggleTextLocate(item)}>
                    <TravelExplore />
                  </IconButton>
                </Tooltip>
              </Box>
            </div>
          )}
          {isExpanded && !isEditing && (
            <div>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
              >
                {item.notes}
              </Typography>
              {item.companyName && (
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "bold", wordBreak: "break-word" }}
                >
                  Company: {item.companyName}
                </Typography>
              )}
              <Grid container spacing={3}>
                {item.description.split(/[,\n]/).filter(pair => pair.trim()).map((pair, index) => {
                  const [key, ...valueParts] = pair.split(":");
                  const trimmedKey = key.trim();
                  const value = valueParts.join(":").trim();

                  return (
                    <Grid
                      item
                      xs={
                        trimmedKey === "Reference Number" ||
                        trimmedKey === "Instructions for Dispatch"
                          ? 12
                          : 6
                      }
                      key={index}
                    >
                      <Typography sx={{ wordBreak: "break-word" }}>
                        {item.description.includes(key) ? (
                          <span style={{ fontWeight: "bold" }}>{key}</span>
                        ) : (
                          <span>{key}</span>
                        )}
                        {value && `: ${value}`}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
              {item?.textLocate?.updateStatus && (
                <Tooltip title="Pinged Location" placement="top">
                  <IconButton onClick={() => onToggleLocation(item)}>
                    <Map />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit" placement="top">
                <IconButton onClick={() => onEditStart(item)} size="small">
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive" placement="top">
                <IconButton onClick={() => onArchive(item)} size="small">
                  <Archive />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove" placement="top">
                <IconButton onClick={() => onRemove(columnId, item._id)} size="small">
                  <Cancel />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="top">
                <IconButton onClick={() => onDelete(item._id)} size="small">
                  <Delete />
                </IconButton>
              </Tooltip>
            </div>
          )}
          <Box>
            <Tooltip title="Copy Pro Number" placement="top">
              <IconButton
                size="small"
                onClick={() =>
                  handleCopyToClipboard(
                    getDescriptionValue(item.description, "Pro Number"),
                    "Pro Number"
                  )
                }
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Customer Email" placement="top">
              <IconButton
                size="small"
                onClick={() =>
                  handleCopyToClipboard(
                    getDescriptionValue(item.description, "Customer Email"),
                    "Customer Email"
                  )
                }
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Dispatcher Email" placement="top">
              <IconButton
                size="small"
                onClick={() =>
                  handleCopyToClipboard(
                    getDescriptionValue(item.description, "Dispatcher Email"),
                    "Dispatcher Email"
                  )
                }
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </div>
      </Paper>
    </div>
  );
});

const DispatchBoard = () => {
  const theme = useTheme();
  const isConnected = useSelector((state) => state.webSocket.isConnected);

  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  // Column States
  const [columns, setColumns] = useState([]); // All columns
  const [bookingColumn, setBookingColumn] = useState({});
  const [atPickupColumn, setAtPickupColumn] = useState({});
  const [inTransitColumn, setInTransitColumn] = useState({});
  const [atDeliveryColumn, setAtDeliveryColumn] = useState({});
  const [completeColumn, setCompleteColumn] = useState({});
  const [problemColumn, setProblemColumn] = useState({});
  const [projectColumn, setProjectColumn] = useState({});

  // Editing States
  const [editCardId, setEditCardId] = useState(null);

  const [activeFilters, setActiveFilters] = useState([]);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [textLocateOpen, setTextLocateOpen] = useState(false);
  const [cardToText, setCardToText] = useState(null);
  const [locationOpen, setLocationOpen] = useState(false)
  const [cardToLocate, setCardToLocate] = useState(null)

  const columnsRef = useRef(columns);

  // Mutations
  const [addCard] = useAddCardToBookedColumnMutation();

  const [updateCard] = useUpdateCardMutation();

  const [archiveCard] = useArchiveCardMutation();

  const [deleteCard] = useDeleteCardMutation();

  const [removeCardFromColumn] =
    useRemoveCardFromColumnMutation();

  const [updateColumn] =
    useUpdateColumnMutation();
  
  const { enqueueSnackbar } = useSnackbar();

  const defaultCardColor = theme.palette.primary[1000];

  // Websocket handling
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const socket = setupWebSocket(
      process.env.REACT_APP_WEBSOCKET_URL,
      "Dispatch2"
    );

    // On page load/websocket connect
    socket.on("initialState", (columns) => {
      syncColumns(columns);
    });

    // On column update/websocket emit
    socket.on("columnUpdated", (data) => { // Data returned as { columnId: parentColumn._id, column: parentColumn }
      // Skip updates while user is editing a card to avoid interrupting typing
      if (editCardId) return;
      
      if (data.updatedColumns) { // If data is returned
        for (const column of data.updatedColumns) { // For every updated column returned
          handleColumnUpdating(column.name, column);
        }
      } else {
        handleColumnUpdating(data.column.name, data.column);
      }
    });

    // On textLocate webhook response
    socket.on("webhookResponse", (card) => {
      handleUpdateCardById(card, columnsRef.current);
    });
    
    return () => socket.disconnect(); // Cleanup on unmount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  // Togglers
  const toggleFilterDialog = () => setFilterDialogOpen((prev) => !prev);
  const toggleTextLocate = useCallback((card) => {
    textLocateOpen ? setCardToText(null) : setCardToText(card);
    setTextLocateOpen(prev => !prev);
  }, [textLocateOpen]);
  const toggleLocationOpen = useCallback((card) => {
    cardToLocate ? setCardToLocate(null) : setCardToLocate(card);
    setLocationOpen((prev) => !prev);
  }, [cardToLocate]);

  // Handlers
  const handleColumnSetters = (columns) => {
    setBookingColumn(columns.find((column) => column.name === "Booked"));
    setAtPickupColumn(columns.find((column) => column.name === "At Pickup"));
    setInTransitColumn(columns.find((column) => column.name === "In Transit"));
    setAtDeliveryColumn(columns.find((column) => column.name === "At Delivery"));
    setCompleteColumn(columns.find((column) => column.name === "Complete"));
    setProblemColumn(columns.find((column) => column.name === "Problem"));
    setProjectColumn(columns.find((column) => column.name === "Project"));
  };

  const syncColumns = useCallback((nextColumns) => {
    setColumns(nextColumns);
    handleColumnSetters(nextColumns);
    columnsRef.current = nextColumns;
  }, []);

  const handleColumnUpdating = (columnName, column) => {
    const currentColumns = columnsRef.current;
    const nextColumns = currentColumns.map((col) =>
      col.name === columnName ? column : col
    );

    syncColumns(nextColumns);
  };

  const handleUpdateCardById = (card, currentColumns = columnsRef.current) => {
    const columnWithCard = currentColumns.find((column) =>
      column.items?.some?.((item) => item._id === card._id)
    );

    if (!columnWithCard) {
      return;
    }

    const updatedColumn = {
      ...columnWithCard,
      items: columnWithCard.items.map((item) =>
        item._id === card._id ? card : item
      ),
    };

    const nextColumns = currentColumns.map((col) =>
      col._id === updatedColumn._id || col.name === updatedColumn.name
        ? updatedColumn
        : col
    );

    syncColumns(nextColumns);
  };

  const handleGetColumnFromDroppableId = (droppableId, columnList = columnsRef.current) => {
    switch (droppableId) {
      case "column-1":
        return columnList.find((column) => column.name === "Booked");
      case "column-2":
        return columnList.find((column) => column.name === "At Pickup");
      case "column-3":
        return columnList.find((column) => column.name === "In Transit");
      case "column-4":
        return columnList.find((column) => column.name === "At Delivery");
      case "column-5":
        return columnList.find((column) => column.name === "Complete");
      case "column-6":
        return columnList.find((column) => column.name === "Problem");
      case "column-7":
        return columnList.find((column) => column.name === "Project");
      default:
        break;
    }
  };

  const handleAddCard = async () => {
    const bookedColumn = handleGetColumnFromDroppableId("column-1", columnsRef.current);
    if (!bookedColumn) {
      enqueueSnackbar("Booked column unavailable", { variant: "error" });
      return;
    }

    const newCard = {
      title: "",
      subtitle: "",
      notes: "",
      description: "",
      color: theme.palette.primary[1000],
    };

    const tempId = `temp-${Date.now()}`;
    const optimisticCard = { ...newCard, _id: tempId };
    const previousColumns = columnsRef.current;
    const optimisticColumns = previousColumns.map((col) =>
      col._id === bookedColumn._id
        ? { ...col, items: [optimisticCard, ...(col.items || [])] }
        : col
    );

    syncColumns(optimisticColumns);

    try {
      const response = await addCard(newCard).unwrap();
      const savedCard = response?.card;

      if (!savedCard) {
        throw new Error("Card not returned from server");
      }

      const reconciledColumns = columnsRef.current.map((col) =>
        col._id === bookedColumn._id
          ? {
              ...col,
              items: col.items.map((item) =>
                item._id === tempId ? savedCard : item
              ),
            }
          : col
      );

      syncColumns(reconciledColumns);
      enqueueSnackbar("Dispatch Card Added");
    } catch (error) {
      console.error("Error adding card:", error);
      enqueueSnackbar("Failed to add card", { variant: "error" });
      syncColumns(previousColumns);
    }
  };

  const handleSaveCard = useCallback(async (cardId, updates) => {
    // Optimistic Update
    const previousColumns = columnsRef.current;
    const columnWithCard = previousColumns.find((column) =>
      column.items?.some?.((item) => item._id === cardId)
    );

    if (columnWithCard) {
      const updatedCard = {
        ...columnWithCard.items.find((item) => item._id === cardId),
        ...updates,
      };

      const optimisticColumns = previousColumns.map((col) =>
        col._id === columnWithCard._id
          ? {
              ...col,
              items: col.items.map((item) =>
                item._id === cardId ? updatedCard : item
              ),
            }
          : col
      );

      syncColumns(optimisticColumns);
    }

    setEditCardId(null);

    try {
      await updateCard({ cardId, cardUpdates: updates }).then(
        () => {
          enqueueSnackbar(`Card Updated`);
        }
      );
    } catch (error) {
      console.error("Error Saving Card:", error);
      syncColumns(previousColumns); // Revert on error
      setEditCardId(cardId); // Re-open edit mode
      enqueueSnackbar("Failed to save card", { variant: "error" });
    }
  }, [updateCard, enqueueSnackbar, syncColumns]);

  const handleEditCard = useCallback((item) => {
    setEditCardId(item._id);
  }, []);

  const handleCancelEditCard = useCallback(() => {
    setEditCardId(null);
  }, []);

  const handleRemoveCard = useCallback(async (columnId, cardId) => {
    const confirmed = window.confirm(
      "Confirm: Remove this card? This will delete the card from the board, but save it in the database."
    );

    if (!confirmed) return;

    const previousColumns = columnsRef.current;
    const optimisticColumns = previousColumns.map((col) =>
      col._id === columnId
        ? { ...col, items: col.items.filter((item) => item._id !== cardId) }
        : col
    );

    syncColumns(optimisticColumns);

    try {
      await archiveCard(cardId).unwrap();
      await removeCardFromColumn({ columnId, cardId }).unwrap();
      enqueueSnackbar(`Card Removed`);
    } catch (error) {
      console.error("Error Removing Card:", error);
      enqueueSnackbar("Failed to remove card", { variant: "error" });
      syncColumns(previousColumns);
    }
  }, [archiveCard, removeCardFromColumn, enqueueSnackbar, syncColumns]);

  const handleDeleteCard = useCallback(async (cardId) => {
    const confirmed = window.confirm(
      "Confirm: Delete card? This will will remove the card from the database completely."
    );

    if (!confirmed) return;

    const previousColumns = columnsRef.current;
    const columnWithCard = previousColumns.find((col) =>
      col.items?.some?.((item) => item._id === cardId)
    );

    const optimisticColumns = columnWithCard
      ? previousColumns.map((col) =>
          col._id === columnWithCard._id
            ? { ...col, items: col.items.filter((item) => item._id !== cardId) }
            : col
        )
      : previousColumns;

    syncColumns(optimisticColumns);

    try {
      await deleteCard(cardId).unwrap();
      enqueueSnackbar(`Card Deleted`);
    } catch (error) {
      console.error("Error Deleting Card:", error);
      enqueueSnackbar("Failed to delete card", { variant: "error" });
      syncColumns(previousColumns);
    }
  }, [deleteCard, enqueueSnackbar, syncColumns]);

  const handleNewArchiveCard = useCallback(async (item) => {
    if (!item.title || !item.subtitle || !item.description || !item.notes) {
      const confirmed = window.confirm(
        "Some required fields are empty. Are you sure you want to proceed?"
      );

      if (!confirmed) {
        return;
      }
    }

    const previousColumns = columnsRef.current;
    const columnWithCard = previousColumns.find((col) =>
      col.items?.some?.((card) => card._id === item._id)
    );

    if (columnWithCard) {
      const optimisticColumns = previousColumns.map((col) =>
        col._id === columnWithCard._id
          ? {
              ...col,
              items: col.items.map((card) =>
                card._id === item._id
                  ? { ...card, archived: true, archivedBy: card.archivedBy || { _id: "local" } }
                  : card
              ),
            }
          : col
      );

      syncColumns(optimisticColumns);
    }

    try {
      await archiveCard(item._id).unwrap();
      enqueueSnackbar(`${item.title} Archived`);
    } catch (error) {
      console.error("Error Archiving Card:", error);
      enqueueSnackbar("Failed to archive card", { variant: "error" });
      syncColumns(previousColumns);
    }
  }, [archiveCard, enqueueSnackbar, syncColumns]);

  const handleDragEnd = async (result) => {

    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const currentColumns = columnsRef.current;
    const sourceColumn = handleGetColumnFromDroppableId(source.droppableId, currentColumns);
    const destColumn = handleGetColumnFromDroppableId(destination.droppableId, currentColumns);

    if (!sourceColumn || !destColumn) {
      return;
    }

    const sourceItems = Array.from(sourceColumn.items || []);
    const destinationItems =
      sourceColumn._id === destColumn._id
        ? sourceItems
        : Array.from(destColumn.items || []);

    // 1. Find REAL source index (safest via ID) and Remove Item
    const realSourceIndex = sourceItems.findIndex(item => item._id === result.draggableId);
    if (realSourceIndex === -1) return;
    
    const [movedItem] = sourceItems.splice(realSourceIndex, 1);

    // 2. Calculate REAL destination index
    let realDestinationIndex = destination.index;

    // If filters are active, translate filtered-index to full-list-index
    // NOTE: This calculation happens on 'destinationItems' which already has the item REMOVED.
    if (activeFilters.length > 0) {
      const filteredDestItems = destinationItems.filter(
        (item) =>
          item && activeFilters.includes(item.color)
      );

      if (filteredDestItems.length === 0) { 
        // Or just end of list. Let's go with end of list for simplicity.
        realDestinationIndex = destinationItems.length;
      } else if (destination.index >= filteredDestItems.length) {
        // Dropped after the last filtered item
        const lastFilteredItem = filteredDestItems[filteredDestItems.length - 1];
        const lastFilteredItemIndex = destinationItems.findIndex(i => i._id === lastFilteredItem._id);
        realDestinationIndex = lastFilteredItemIndex + 1;
      } else {
        // Dropped before a specific filtered item
        const targetItem = filteredDestItems[destination.index];
        realDestinationIndex = destinationItems.findIndex(i => i._id === targetItem._id);
      }
    }

    destinationItems.splice(realDestinationIndex, 0, movedItem);

    const optimisticColumns = currentColumns.map((col) => {
      if (col._id === sourceColumn._id && col._id === destColumn._id) {
        return { ...col, items: destinationItems };
      }
      if (col._id === sourceColumn._id) {
        return { ...col, items: sourceItems };
      }
      if (col._id === destColumn._id) {
        return { ...col, items: destinationItems };
      }
      return col;
    });

    syncColumns(optimisticColumns);

    try {
      await updateColumn({
        updatedColumn: {
          sourceColumnId: sourceColumn._id,
          destinationColumnId: destColumn._id,
          cardId: result.draggableId,
          newPosition: realDestinationIndex, // Send REAL index to server
        },
      }).unwrap();
    } catch (error) {
      console.error("Error moving card:", error);
      syncColumns(currentColumns);
      enqueueSnackbar("Failed to move card", { variant: "error" });
    }
  };

  const handleFilterChange = (color) => {
    if (activeFilters.includes(color)) {
      setActiveFilters((prev) => prev.filter((c) => c !== color));
    } else {
      setActiveFilters((prev) => [...prev, color]);
    }
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
  };

  // HTML for columns
  const column = (column, columnId) => {
    return (
      <Grid item xs={12} sm={6} md={6} lg={4}>
        <Paper
          elevation={3}
          sx={{
            mb: "15px",
            pb: "5px",
            backgroundColor: theme.palette.background.secondary,
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            align="center"
            gutterBottom
            sx={{ backgroundColor: theme.palette.primary[900] }}
          >
            {column.name}
          </Typography>
          {/* FIXES ERROR BUT CAUSES NEW ONE */}
          <Droppable droppableId={columnId} /* droppableId={column._id} */>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {column.items &&
                  column.items
                    .filter(
                      (item) =>
                        item &&
                        (activeFilters.length === 0 ||
                          activeFilters.includes(item.color))
                    )
                    .map((item, index) => {
                      const isEditing = editCardId === item._id;

                      return (
                        <Draggable
                          key={item._id}
                          draggableId={item._id}
                          index={index}
                        >
                          {(provided) => (
                            <DispatchCard
                              item={item}
                              isEditing={isEditing}
                              onEditStart={handleEditCard}
                              onEditCancel={handleCancelEditCard}
                              onSave={handleSaveCard}
                              onDelete={handleDeleteCard}
                              onArchive={handleNewArchiveCard}
                              onRemove={handleRemoveCard}
                              onToggleTextLocate={toggleTextLocate}
                              onToggleLocation={toggleLocationOpen}
                              provided={provided}
                              defaultCardColor={defaultCardColor}
                              columnId={column._id}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </Paper>
      </Grid>
    );
  };

  if (
    !bookingColumn ||
    !atPickupColumn ||
    !inTransitColumn ||
    !atDeliveryColumn ||
    !completeColumn ||
    !problemColumn ||
    !projectColumn
  )
    return null;

  return (
    <Box p="1.5rem 2.5rem" sx={{ width: "100%" }}>
      <Header
        title="DISPATCH BOARD V.2"
        subtitle="Manage shipment cards and status"
      />
      <Typography
        variant="h6"
        component="h2"
        gutterBottom
        sx={{ paddingBottom: 1 }}
      >
        WebSocket Status:{" "}
        {isConnected ? (
          <Typography sx={{ color: "#00deeb" }}>Connected</Typography>
        ) : (
          <Typography sx={{ color: "#FF0000" }}>Disconnected</Typography>
        )}
      </Typography>
      <Grid
        container
        spacing={1}
        sx={{ backgroundColor: theme.palette.background.alt, padding: 2 }}
      >
        <Grid item xs={12} md={2}>
          <Grid item container spacing={8}>
            <Grid item xs={12} sm={4} md={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleAddCard}
              >
                Add Card
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={toggleFilterDialog}
              >
                Filter Colors
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
          {filterDialogOpen && (
            <Dialog open={filterDialogOpen} onClose={toggleFilterDialog}>
              <DialogTitle>Filter by Color</DialogTitle>
              <DialogContent>
                {PREDEFINED_COLORS.map((color) => (
                  <FormControlLabel
                    key={color.name}
                    control={
                      <Checkbox
                        checked={activeFilters.includes(color.color)}
                        onChange={() => handleFilterChange(color.color)}
                        name={color.name}
                        color="primary"
                      />
                    }
                    label={color.name}
                  />
                ))}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={toggleFilterDialog}
                  variant="contained"
                  color="error"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Grid>
        <Grid item xs={12} md={12}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Grid container spacing={3}>
              {column(bookingColumn, "column-1")}
              {column(atPickupColumn, "column-2")}
              {column(inTransitColumn, "column-3")}
              {column(atDeliveryColumn, "column-4")}
              {column(completeColumn, "column-5")}
              {column(problemColumn, "column-6")}
              {column(projectColumn, "column-7")}
            </Grid>
          </DragDropContext>
        </Grid>
      </Grid>
      {textLocateOpen && (
        <TextLocateForm
          card={cardToText}
          onClose={toggleTextLocate}
        />
      )}
      {locationOpen && (
        <DispatchPingLocation 
          card={cardToLocate}
          onClose={toggleLocationOpen}
          mapsKey={mapsKey}
        />
      )}
    </Box>
  );
};

export default DispatchBoard;